import React, { useState } from 'react'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import GoogleDriveLogo from '/assets/image/google-drive.svg'
import { CreateSimpleStorage } from './CreateSimpleStorage'
import { CreateGoogleDriveStorage } from './CreateGoogleDriveStorage'

type SetupStorageDialogProps = {
  orgId?: string | number
  triggerVariant?: 'link' | 'default'
  triggerClassName?: string
  customTrigger?: React.ReactNode
}

export const SetupStorageDialog = ({
  triggerVariant = 'default',
  triggerClassName,
  customTrigger,
}: SetupStorageDialogProps) => {
  const [open, setOpen] = useState(false)

  const handleSuccess = () => {
    setOpen(false)
  }

  const triggerButton =
    customTrigger ||
    (triggerVariant === 'link' ? (
      <Button
        variant="link"
        className={triggerClassName || 'underline decoration-wavy text-primary'}
      >
        Create Storage
      </Button>
    ) : (
      <Button className={triggerClassName}>
        <Plus className="w-4 h-4 mr-2" />
        Create Storage
      </Button>
    ))

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{triggerButton}</DialogTrigger>

      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Create Storage
          </DialogTitle>
          <DialogDescription>
            Configure a new storage volume for your server
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="simple" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 gap-4 h-auto bg-transparent border-none">
            <TabsTrigger
              value="simple"
              className="cursor-pointer flex flex-col bg-transparent hover:bg-muted/50 items-start justify-start p-3 h-auto min-h-[4.5rem] gap-1 data-[state=active]:bg-background data-[state=active]:border-2 data-[state=active]:border-primary data-[state=active]:text-foreground text-foreground rounded-lg"
            >
              <div className="flex items-center">
                <span className="font-medium text-foreground">
                  Simple Storage
                </span>
              </div>
              <p className="text-xs text-muted-foreground font-normal text-left line-clamp-2">
                Basic storage for your inference workloads
              </p>
            </TabsTrigger>
            <TabsTrigger
              value="google_drive"
              className="cursor-pointer flex flex-col bg-transparent hover:bg-muted/50 items-start justify-start p-3 h-auto min-h-[4.5rem] gap-1 data-[state=active]:bg-background data-[state=active]:border-2 data-[state=active]:border-primary data-[state=active]:text-foreground text-foreground rounded-lg"
            >
              <div className="flex items-center gap-1.5">
                <img
                  src={GoogleDriveLogo}
                  alt="Google Drive"
                  className="w-4 h-4"
                />
                <span className="font-medium text-foreground">
                  Google Drive
                </span>
              </div>
              <p className="text-xs text-muted-foreground font-normal text-left line-clamp-2">
                Connect to your Google Drive for storage
              </p>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="simple" className="mt-4">
            <CreateSimpleStorage onSuccess={handleSuccess} />
          </TabsContent>

          <TabsContent value="google_drive" className="mt-4">
            <CreateGoogleDriveStorage onSuccess={handleSuccess} />
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
